import React from 'react';

import FormContainer from 'components/LoanForm/FormContainer';
import FormNavigation from 'components/FormNavigation';
import { FlowComponentType } from 'routes/types';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useCurrentFlow from 'hooks/useCurrentFlow';
import { authOtp, sendSmsCode } from 'thunks';
import { useSelector } from 'react-redux';
import { getAuthData } from 'selectors/getAuthData';
import { getYourContactData } from 'selectors/getYourContact';
import { Environments, getEnvironment } from 'utils/getEnvironment';
import { useResendLogic } from 'hooks/useResendLogic';
import { ContactType } from 'handlers/yourContact';

import VerifyCode from 'components/VerifyCode';
import { ContinueMode } from 'components/VerifyCode/VerifyCode';
import ResendConfirmationModal from 'components/ResendConfirmationModal';
import { useResendConfirmationModal } from 'hooks/useResendConfirmationModal';

import { FINWISE_PRIVACY_NOTICE_URL } from 'constants/urls';

import styles from './VerifyPhoneNumber.module.scss';

const VerifyPhoneNumber = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { isCardFlow } = useCurrentFlow();
  const { canResend, getFormattedTime } = useResendLogic(ContactType.Phone);

  const { phoneId } = useSelector(getAuthData);
  const { phone_number: phoneNumber } = useSelector(getYourContactData);

  const performSmsResend = async () => {
    await dispatchWithUnwrap(sendSmsCode({ phoneNumber }));
    recordSent(phoneNumber);
  };

  const { onResendCode, resendConfirmationModalProps, recordSent } = useResendConfirmationModal({
    contactType: ContactType.Phone,
    contactValue: phoneNumber,
    performResend: performSmsResend,
  });

  const onVerify = async (code: string) => {
    const authResponse = await dispatchWithUnwrap(authOtp({ code, phoneOrEmailId: phoneId!, phoneNumber }));
    if (authResponse.verified) handleNext();

    return authResponse.verified;
  };

  const resendButtonProps = {
    disabled: !canResend,
    ...(!canResend && { text: `Resend code (${getFormattedTime()})` }),
  };

  return (
    <>
      <ResendConfirmationModal {...resendConfirmationModalProps} />

      <FormNavigation {...navigationInfo} {...(isCardFlow && { title: 'Sign Up' })} />
      <FormContainer
        title={isCardFlow ? 'Verify phone number' : 'Verify and consent'}
        subtitle="Enter the authorization code sent to your phone to allow us to retrieve your data."
      >
        <VerifyCode
          handleSessionError={handleNext}
          onResendCode={onResendCode}
          onVerify={onVerify}
          continueMode={getEnvironment() === Environments.Staging ? ContinueMode.OnEnterKey : ContinueMode.Manual}
          resendButtonProps={resendButtonProps}
          consent={
            !isCardFlow ? (
              <p className={styles.consent}>
                By clicking Agree and Continue, I hereby consent to the Plannery{' '}
                <a href="https://www.planneryapp.com/esign-agreement" target="_blank" rel="noreferrer">
                  E-Sign Agreement
                </a>
                ,{' '}
                <a href="https://www.planneryapp.com/terms-of-service" target="_blank" rel="noreferrer">
                  Terms of Service
                </a>
                ,{' '}
                <a href="https://www.planneryapp.com/privacy-policy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                , and{' '}
                <a href="https://www.planneryapp.com/communication-policy" target="_blank" rel="noreferrer">
                  Communication Policy
                </a>{' '}
                and the{' '}
                <a href={FINWISE_PRIVACY_NOTICE_URL} target="_blank" rel="noreferrer">
                  FinWise Bank Privacy Policy
                </a>
                , and I am providing written consent under the FCRA for Plannery to obtain consumer report information
                from my credit profile.
              </p>
            ) : undefined
          }
        />
      </FormContainer>
    </>
  );
};

export default VerifyPhoneNumber;
